import React, { useMemo, useState, useEffect } from "react";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ISwivuStore } from "@/types/SwivuStore";
import { MetaItemConfig, ComboboxMetaItemConfig, TagsMetaItemConfig } from "../constants/editorMetaItems";
import { useDebounce } from "@/react/hooks/useDebounce";
import { Input } from "@/modern/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/modern/components/ui/command";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { Button } from "@/modern/components/ui/button";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/modern/utils";
import { getFontAwesomeClassName } from "@/other/utils/utils";
import { Model } from "@bryntum/schedulerpro";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { Combobox } from "@/modern/components/ui/combobox";
import { EditorTagField } from "./EditorTagField";
import { EditorFormField } from "./EditorFormField";
import { EditorSection } from "./EditorSection";

export function EditorMetaDetails({
  record,
  storeKey,
  metaItems,
  isDirectUpdates = true,
  onFieldChange,
  isFieldDisabled = false,
}: {
  record: EntityRecord;
  /** The entity's main store key — used to track external record updates and sync local field state. */
  storeKey: string;
  metaItems: MetaItemConfig[];
  isDirectUpdates?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: MetaItemConfig) => void;
  isFieldDisabled?: boolean;
}) {
  const { version: entityUpdatesVersion } = useSwivuStore<EntityRecord>({ storeId: storeKey });

  return (
    <div className="grid grid-cols-3 gap-5">
      {metaItems.map((itemConfig, index) => (
        <MetaItem
          key={itemConfig.label || index}
          record={record}
          itemConfig={itemConfig}
          isDirectUpdates={isDirectUpdates}
          onFieldChange={onFieldChange}
          isFieldDisabled={isFieldDisabled}
          entityUpdatesVersion={entityUpdatesVersion}
        />
      ))}
    </div>
  );
}

const MetaItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  entityUpdatesVersion,
}: {
  record: EntityRecord;
  itemConfig: MetaItemConfig;
  isDirectUpdates?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: MetaItemConfig) => void;
  isFieldDisabled?: boolean;
  entityUpdatesVersion: number;
}) => {
  if (itemConfig.type === "tags") {
    return (
      <TagsMetaItem
        record={record}
        itemConfig={itemConfig}
        isDirectUpdates={isDirectUpdates}
        onFieldChange={onFieldChange}
        isFieldDisabled={isFieldDisabled}
        entityUpdatesVersion={entityUpdatesVersion}
      />
    );
  }

  if (itemConfig.type === "input") {
    return (
      <InputMetaItem
        record={record}
        itemConfig={itemConfig}
        isDirectUpdates={isDirectUpdates}
        onFieldChange={onFieldChange}
        isFieldDisabled={isFieldDisabled}
        entityUpdatesVersion={entityUpdatesVersion}
      />
    );
  }

  if (itemConfig.type === "icon") {
    return (
      <IconMetaItem
        record={record}
        itemConfig={itemConfig}
        isDirectUpdates={isDirectUpdates}
        onFieldChange={onFieldChange}
        isFieldDisabled={isFieldDisabled}
        entityUpdatesVersion={entityUpdatesVersion}
      />
    );
  }

  return (
    <ComboboxMetaItem
      record={record}
      itemConfig={itemConfig}
      isDirectUpdates={isDirectUpdates}
      onFieldChange={onFieldChange}
      isFieldDisabled={isFieldDisabled}
      entityUpdatesVersion={entityUpdatesVersion}
    />
  );
};

const TagsMetaItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  entityUpdatesVersion,
}: {
  record: EntityRecord;
  itemConfig: TagsMetaItemConfig;
  isDirectUpdates?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: MetaItemConfig) => void;
  isFieldDisabled?: boolean;
  entityUpdatesVersion: number;
}) => {
  const { fieldConfig, label } = itemConfig;
  const [localValue, setLocalValue] = useState<number[]>(record[fieldConfig.name] ?? []);

  useEffect(() => {
    setLocalValue(record[fieldConfig.name] ?? []);
  }, [record, fieldConfig.name, entityUpdatesVersion]);

  const handleValueChange = (ids: number[]) => {
    setLocalValue(ids);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, ids);
    }
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value: ids }, record.data, itemConfig);
    }
  };

  return (
    <EditorSection withDivider className="col-span-full" dividerClassName="col-span-full">
      <EditorFormField label={label}>
        <EditorTagField
          tagConfig={itemConfig}
          value={localValue}
          onValueChange={handleValueChange}
          disabled={isFieldDisabled}
        />
      </EditorFormField>
    </EditorSection>
  );
};

const ComboboxMetaItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  entityUpdatesVersion,
}: {
  record: EntityRecord;
  itemConfig: ComboboxMetaItemConfig;
  isDirectUpdates?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: MetaItemConfig) => void;
  isFieldDisabled?: boolean;
  entityUpdatesVersion: number;
}) => {
  const context = useSwivuBryntumContext();
  const { project, editorHandler, entityManager, viewManager } = context;
  const { storeKey, filterFn, fieldConfig, label, placeholder, multiSelect, createOnUnmatched, optionItemActionRenderer, disabled } = itemConfig;
  const store = project.getCrudStore(storeKey) as unknown as ISwivuStore<EntityRecord>;
  const { version } = useSwivuStore({ storeId: storeKey });

  const isDisabled = disabled ? disabled(record, context) : false;

  const [localValue, setLocalValue] = useState<number | string | (number | string)[] | null>(record[fieldConfig.name]);

  useEffect(() => {
    setLocalValue(record[fieldConfig.name]);
  }, [record, fieldConfig.name, entityUpdatesVersion]);

  const options = useMemo(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
    return store
      .query((record: EntityRecord) => filterFn(record) && !record.isPhantom && !record.isGroupHeader && (canShowArchivedRecords || !record.archived))
      .map((record: EntityRecord) => ({
        label: record[fieldConfig.displayField],
        value: record[fieldConfig.valueField]
      }));
  }, [version, store, filterFn, fieldConfig]);

  const handleValueChange = (value: number | string | (number | string)[] | null) => {
    setLocalValue(value);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, value);
    }
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value }, record.data, itemConfig);
    }
  };

  const comboboxProps = {
    options,
    value: localValue ?? undefined,
    onValueChange: handleValueChange,
    placeholder: placeholder || `Select ${label}`,
    searchPlaceholder: `Search ${label}...`,
    popoverContentClassName: cn("z-[--swivu-bryntum-z-index] w-[var(--radix-popover-trigger-width)]"),
    disabled: isDisabled || isFieldDisabled,
    createOnUnmatched: createOnUnmatched ? (value: string) => createOnUnmatched({ value, record, editorHandler, project, viewManager }) : undefined,
    clearable: true,
    optionItemActionRenderer: optionItemActionRenderer ? (option: { value: number | string; label: string }) => {
      const optionRecord = store.getById(option.value);
      return optionRecord ? optionItemActionRenderer({ record: optionRecord, editorHandler, entityManager }) : null;
    } : undefined,
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium whitespace-nowrap flex-shrink-0 self-start">{label}</label>
      <Combobox
        {...comboboxProps}
        multiSelect={multiSelect}
        showBadges={false}
        maxDisplay={7}
        size="lg"
        enableRemoveButton
        hideAddButton
        buttonSelectedClassName="px-3"
      />
    </div>
  );
};

const InputMetaItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  entityUpdatesVersion,
}: {
  record: EntityRecord;
  itemConfig: Extract<MetaItemConfig, { type: "input" }>;
  isDirectUpdates?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: MetaItemConfig) => void;
  isFieldDisabled?: boolean;
  entityUpdatesVersion: number;
}) => {
  const { fieldName, label, placeholder, inputType = "text" } = itemConfig;

  const initialValue = record[fieldName] || "";
  const [localValue, setLocalValue] = useState<string>(initialValue);
  const debouncedValue = useDebounce(localValue, 500);

  useEffect(() => {
    setLocalValue(record[fieldName] || "");
  }, [record, fieldName, entityUpdatesVersion]);

  useEffect(() => {
    const currentRecordValue = record[fieldName] || "";
    if (debouncedValue !== currentRecordValue && debouncedValue !== initialValue) {
      if (isDirectUpdates) {
        record.set(fieldName, debouncedValue);
      }
      if (onFieldChange) {
        onFieldChange({ fieldName, value: debouncedValue }, record.data, itemConfig);
      }
    }
  }, [debouncedValue, record, fieldName, initialValue, onFieldChange, itemConfig]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") (e.target as HTMLInputElement).blur();
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium whitespace-nowrap flex-shrink-0 self-start">{label}</label>
      <Input
        type={inputType}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "Enter value..."}
        disabled={isFieldDisabled}
        variant="outline"
        className="w-full h-swivu-input"
      />
    </div>
  );
};

const IconMetaItem = ({
  record,
  itemConfig,
  isDirectUpdates = false,
  onFieldChange,
  isFieldDisabled = false,
  entityUpdatesVersion,
}: {
  record: EntityRecord;
  itemConfig: Extract<MetaItemConfig, { type: "icon" }>;
  isDirectUpdates?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange?: (changes: { fieldName: string; value: any }, allFieldData: Record<string, any>, config: MetaItemConfig) => void;
  isFieldDisabled?: boolean;
  entityUpdatesVersion: number;
}) => {
  const { project } = useSwivuBryntumContext();
  const { storeKey, filterFn, fieldConfig, label } = itemConfig;
  const store = project.getCrudStore(storeKey) as unknown as ISwivuStore<EntityRecord>;
  const { version } = useSwivuStore<Model>({ storeId: storeKey, subscriberFn: ({ record: changedRecord }) => changedRecord.id === record.id });
  const [open, setOpen] = useState(false);

  const [selectedValue, setSelectedValue] = useState<number | string | null>(record[fieldConfig.name]);

  useEffect(() => {
    setSelectedValue(record[fieldConfig.name]);
  }, [record, fieldConfig.name, entityUpdatesVersion]);

  const teamColor = record._team?.color || "";

  const options = useMemo(() => {
    return store
      .query((record: EntityRecord) => filterFn(record) && !record.isPhantom && !record.isGroupHeader)
      .map((record: EntityRecord) => {
        const tagValue = record[fieldConfig.displayField];
        const iconClassName = getFontAwesomeClassName(tagValue);
        return {
          label: "",
          value: record[fieldConfig.valueField],
          icon: <i className={`fa ${iconClassName}`} style={{ color: teamColor }} />
        };
      });
  }, [version, store, filterFn, fieldConfig, teamColor]);

  const selectedIconNode = useMemo(() => {
    if (selectedValue) {
      const selectedRecord = store.getById(selectedValue);
      if (selectedRecord) {
        const tagValue = selectedRecord[fieldConfig.displayField];
        const iconClassName = getFontAwesomeClassName(tagValue);
        return <i className={`fa ${iconClassName}`} style={{ color: teamColor }} />;
      }
    }
    return null;
  }, [selectedValue, fieldConfig, store, teamColor]);

  const handleValueChange = (value: number | string | (number | string)[] | null) => {
    setSelectedValue(value as number | string | null);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, value);
    }
    setOpen(false);
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value }, record.data, itemConfig);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedValue(null);
    if (isDirectUpdates) {
      record.set(fieldConfig.name, null);
    }
    if (onFieldChange) {
      onFieldChange({ fieldName: fieldConfig.name, value: null }, record.data, itemConfig);
    }
  };

  const popoverContent = (
    <PopoverContent className="w-[200px] p-0 z-[--swivu-bryntum-z-index]" align="start">
      <Command>
        <CommandInput placeholder={`Search ${label}...`} />
        <CommandList>
          <CommandEmpty>No icons found.</CommandEmpty>
          <CommandGroup>
            {options.map((option) => (
              <CommandItem key={option.value} value={String(option.value)} onSelect={() => handleValueChange(option.value)}>
                <Check className={cn("mr-2 h-4 w-4", selectedValue === option.value ? "opacity-100" : "opacity-0")} />
                <div className="flex items-center gap-2">{option.icon}</div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium whitespace-nowrap flex-shrink-0 self-start">{label}</label>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isFieldDisabled}
                  size="lg"
                  className="relative group w-full justify-between font-normal text-left h-swivu-input px-3"
                >
                  <span className="flex items-center gap-2 truncate">
                    {selectedIconNode || <span className="text-muted-foreground text-base">Select Icon</span>}
                  </span>
                  <div className="flex items-center gap-1">
                    {selectedValue && !isFieldDisabled && (
                      <button
                        type="button"
                        onClick={handleClear}
                        className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 flex items-center justify-center"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </div>
                </Button>
              </PopoverTrigger>
              {popoverContent}
            </Popover>
          </TooltipTrigger>
          <TooltipContent>
            <p>Icon color is inherited from Team</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
